import { ref } from "vue";
import type { Ref } from "vue";

export type PollerLifecycleStatus =
  | "idle"
  | "running"
  | "success"
  | "error"
  | "timeout"
  | "aborted";

export interface UsePollerOptions<T> {
  task: () => Promise<T>;
  isDone: (result: T) => boolean;
  isError?: (result: T) => boolean;
  intervalMs?: number;
  backoffFactor?: number;
  maxIntervalMs?: number;
  maxAttempts?: number;
  timeoutMs?: number;
  jitterRatio?: number; // 0.1 => ±10%
  onUpdate?: (result: T, attempt: number) => void;
  onSuccess?: (result: T, attempt: number) => void;
  onError?: (error: unknown, attempt: number) => void;
  onTimeout?: (attempt: number) => void;
  onAbort?: (attempt: number) => void;
}

export interface UsePollerReturn<T> {
  status: Ref<PollerLifecycleStatus>;
  attempts: Ref<number>;
  lastResult: Ref<T | null>;
  error: Ref<unknown>;
  start: () => Promise<T | null>;
  stop: (reason?: "abort") => void;
  restart: () => Promise<T | null>;
  configure: (opts: Partial<UsePollerOptions<T>>) => void;
  isRunning: () => boolean;
}

export function usePoller<T>(
  baseOptions: UsePollerOptions<T>,
): UsePollerReturn<T> {
  const status = ref<PollerLifecycleStatus>("idle");
  const attempts = ref(0);
  const lastResult = ref<any>(null) as Ref<T | null>;
  const error = ref<unknown>(null);
  let opts = { ...baseOptions } as UsePollerOptions<T>;
  let stopped = false;
  let startTime = 0;

  function isRunning() {
    return status.value === "running";
  }

  function configure(newOpts: Partial<UsePollerOptions<T>>) {
    opts = { ...opts, ...newOpts };
  }

  function stop(reason?: "abort") {
    if (status.value === "running") {
      stopped = true;
      status.value = reason === "abort" ? "aborted" : "idle";
      if (reason === "abort" && opts.onAbort) opts.onAbort(attempts.value);
    }
  }

  async function loop(): Promise<T | null> {
    const intervalBase = opts.intervalMs ?? 1500;
    const maxInterval = opts.maxIntervalMs ?? 8000;
    const backoff = opts.backoffFactor ?? 1.5;
    const maxAttempts = opts.maxAttempts ?? 60;
    const timeoutMs = opts.timeoutMs ?? 90000;
    const jitterRatio = opts.jitterRatio ?? 0.1;
    let interval = intervalBase;

    for (;;) {
      if (stopped) break;
      if (maxAttempts && attempts.value >= maxAttempts) {
        status.value = "timeout";
        if (opts.onTimeout) opts.onTimeout(attempts.value);
        return lastResult.value;
      }
      if (timeoutMs && Date.now() - startTime > timeoutMs) {
        status.value = "timeout";
        if (opts.onTimeout) opts.onTimeout(attempts.value);
        return lastResult.value;
      }
      attempts.value += 1;

      try {
        const result = await opts.task();
        lastResult.value = result;
        if (opts.onUpdate) opts.onUpdate(result, attempts.value);
        if (opts.isError && opts.isError(result)) {
          status.value = "error";
          if (opts.onError) opts.onError(result, attempts.value);
          return result;
        }
        if (opts.isDone(result)) {
          status.value = "success";
          if (opts.onSuccess) opts.onSuccess(result, attempts.value);
          return result;
        }
      } catch (e) {
        error.value = e;
        status.value = "error";
        if (opts.onError) opts.onError(e, attempts.value);
        return lastResult.value;
      }
      // schedule next
      const jitter = 1 + (Math.random() * 2 - 1) * jitterRatio; // 1 ± ratio
      await new Promise<void>((resolve) =>
        setTimeout(resolve, interval * jitter),
      );
      interval = Math.min(interval * backoff, maxInterval);
    }
    return lastResult.value;
  }

  async function start(): Promise<T | null> {
    if (status.value === "running") return lastResult.value;
    stopped = false;
    attempts.value = 0;
    error.value = null;
    lastResult.value = null;
    status.value = "running";
    startTime = Date.now();
    return loop();
  }

  async function restart(): Promise<T | null> {
    stop();
    return start();
  }

  return {
    status,
    attempts,
    lastResult,
    error,
    start,
    stop,
    restart,
    configure,
    isRunning,
  };
}
